import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ServiceCost {
  service: string;
  amount: number;
}

interface TenantUsage {
  tenantId: string;
  tenantName: string;
  emailsSentMonth: number;
  activeAutomations: number;
  totalCustomers: number;
}

interface CostsResponse {
  mtd: { total: number; byService: ServiceCost[] };
  lastMonth: { total: number; byService: ServiceCost[] };
  trend: { month: string; total: number }[];
  tenantUsage: TenantUsage[];
  todayEstUsd: number | null;
  cachedAt: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatMonth(isoDate: string): string {
  const d = new Date(isoDate + 'T12:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function formatCachedAt(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffH = Math.floor(diffMs / 3_600_000);
  const diffM = Math.floor(diffMs / 60_000);
  if (diffH >= 1) return `${diffH}h ago`;
  if (diffM >= 1) return `${diffM}m ago`;
  return 'just now';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PlatformCostsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<CostsResponse>({
      queryKey: ['platform-costs'],
      queryFn: () => api.get('/sa/platform/costs').then((r) => r.data),
      staleTime: 10 * 60 * 1000,
    });

  // ── Derived ─────────────────────────────────────────────────────────────────
  const todayEst = data?.todayEstUsd ?? null;
  const last12Total = data?.trend.reduce((s, r) => s + r.total, 0) ?? null;

  const totalEmailsMonth = data?.tenantUsage.reduce((s, t) => s + t.emailsSentMonth, 0) ?? 0;
  const maxBarAmount =
    data && data.mtd.byService.length > 0
      ? (data.mtd.byService[0]?.amount ?? 1)
      : 1;
  const maxTrendAmount =
    data && data.trend.length > 0
      ? Math.max(...data.trend.map((t) => t.total), 0.01)
      : 1;

  // ── Error state ─────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div>
        <PageHeader title="Platform Costs" />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {extractError(error)} —{' '}
          <button onClick={() => refetch()} className="underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeader title="Platform Costs" />
        <div className="flex items-center gap-3">
          {data?.cachedAt && (
            <span className="text-xs text-zinc-400">
              Data cached {formatCachedAt(data.cachedAt)}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
            Force Refresh
          </button>
        </div>
      </div>

      {/* ── KPI cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
        {[
          {
            label: 'Current Month (MTD)',
            value: data ? formatUsd(data.mtd.total) : null,
            sub: data && data.lastMonth.total > 0
              ? `${data.mtd.total < data.lastMonth.total ? '↓' : '↑'} ${Math.abs(((data.mtd.total - data.lastMonth.total) / data.lastMonth.total) * 100).toFixed(0)}% vs last month`
              : '',
            subColor: data && data.mtd.total < data.lastMonth.total ? 'text-emerald-600' : 'text-red-500',
          },
          {
            label: 'Last Month',
            value: data ? formatUsd(data.lastMonth.total) : null,
            sub: data?.trend.slice(-2, -1)[0]
              ? formatMonth(data.trend.slice(-2, -1)[0]!.month)
              : '',
            subColor: 'text-zinc-400',
          },
          {
            label: 'Today (est.)',
            value: todayEst !== null ? formatUsd(todayEst) : null,
            sub: todayEst !== null && data
              ? `Proj: ${formatUsd((todayEst * 30))}/mo`
              : '',
            subColor: 'text-zinc-400',
          },
          {
            label: 'Last 12 Months',
            value: last12Total !== null ? formatUsd(last12Total) : null,
            sub: last12Total !== null
              ? `Avg: ${formatUsd(last12Total / 12)}/mo`
              : '',
            subColor: 'text-zinc-400',
          },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-white border border-zinc-200 rounded-lg p-4"
          >
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
              {card.label}
            </p>
            {isLoading || card.value === null ? (
              <Skeleton className="h-7 w-24 mb-1" />
            ) : (
              <p className="text-2xl font-bold text-zinc-900">{card.value}</p>
            )}
            {card.sub && (
              <p className={`text-xs mt-1 ${card.subColor}`}>{card.sub}</p>
            )}
          </div>
        ))}
      </div>

      {/* ── Two columns: by-service + trend ───────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 mb-4">
        {/* Cost by service */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-zinc-900 mb-3">
            Cost by AWS Service — MTD
          </h3>
          {isLoading ? (
            <div className="space-y-2.5">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-5 w-full" />
              ))}
            </div>
          ) : (data?.mtd.byService ?? []).length === 0 ? (
            <p className="text-sm text-zinc-400">No cost data available yet.</p>
          ) : (
            <div className="space-y-2.5">
              {(data?.mtd.byService ?? []).map((row) => (
                <div key={row.service}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-zinc-600 truncate max-w-[70%]">{row.service}</span>
                    <span className="font-semibold text-zinc-800">{formatUsd(row.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 rounded-full"
                      style={{ width: `${Math.max((row.amount / maxBarAmount) * 100, 1)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly trend */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-zinc-900 mb-3">Monthly Trend</h3>
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (data?.trend ?? []).length === 0 ? (
            <p className="text-sm text-zinc-400">No trend data available yet.</p>
          ) : (
            <div className="flex items-end gap-2 h-24">
              {(data?.trend ?? []).map((t, i, arr) => {
                const isLast = i === arr.length - 1;
                const heightPct = Math.max((t.total / maxTrendAmount) * 100, 2);
                return (
                  <div key={t.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <span className="text-[9px] text-zinc-400">{formatUsd(t.total)}</span>
                    <div
                      className={`w-full rounded-t ${isLast ? 'bg-violet-500' : 'bg-zinc-200'}`}
                      style={{ height: `${heightPct}%` }}
                      title={`${formatMonth(t.month)}: ${formatUsd(t.total)}`}
                    />
                    <span className={`text-[9px] ${isLast ? 'text-violet-600 font-semibold' : 'text-zinc-400'}`}>
                      {formatMonth(t.month)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Tenant usage table ────────────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">Tenant Usage — Current Month</h3>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (data?.tenantUsage ?? []).length === 0 ? (
          <p className="text-sm text-zinc-400">No tenants found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-zinc-100 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                  <th className="pb-2 pr-4">Tenant</th>
                  <th className="pb-2 pr-4 text-right">Emails Sent</th>
                  <th className="pb-2 pr-4 text-right">Active Automations</th>
                  <th className="pb-2 pr-4 text-right">Customers</th>
                  <th className="pb-2 text-right">Est. Usage %</th>
                </tr>
              </thead>
              <tbody>
                {(data?.tenantUsage ?? []).map((t) => {
                  const usagePct =
                    totalEmailsMonth > 0
                      ? Math.round((t.emailsSentMonth / totalEmailsMonth) * 100)
                      : 0;
                  return (
                    <tr key={t.tenantId} className="border-b border-zinc-50 last:border-0">
                      <td className="py-2 pr-4 font-medium text-zinc-800">{t.tenantName}</td>
                      <td className="py-2 pr-4 text-right text-zinc-500">
                        {t.emailsSentMonth.toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-right text-zinc-500">
                        {t.activeAutomations}
                      </td>
                      <td className="py-2 pr-4 text-right text-zinc-500">
                        {t.totalCustomers.toLocaleString()}
                      </td>
                      <td className="py-2 text-right">
                        <span className="inline-block bg-violet-100 text-violet-700 rounded px-1.5 py-0.5 text-[10px] font-semibold">
                          {usagePct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
